import { capture } from '@/lib/posthog/client'
import type { CompletedTask } from '@/lib/types'

function safe(fn: () => void): void {
  try {
    fn()
  } catch (e) {
    console.warn('[PostHog event] failed', e)
  }
}

export function trackTaskCreated(params: {
  notebookId: string
  hasScheduledDate: boolean
  indentLevel: number
}): void {
  safe(() => {
    capture('task_created', {
      notebook_id: params.notebookId,
      has_scheduled_date: params.hasScheduledDate,
      indent_level: params.indentLevel,
    })
  })
}

export function trackTaskCompleted(params: {
  notebookId: string
  hadDuration: boolean
  timeToCompleteSec?: number
}): void {
  safe(() => {
    const props: Record<string, unknown> = {
      notebook_id: params.notebookId,
      had_duration: params.hadDuration,
    }
    if (params.timeToCompleteSec !== undefined) {
      props.time_to_complete_sec = params.timeToCompleteSec
    }
    capture('task_completed', props)
  })
}

export function trackTaskScheduled(params: {
  notebookId: string
  daysAhead: number
}): void {
  safe(() => {
    capture('task_scheduled', {
      notebook_id: params.notebookId,
      days_ahead: params.daysAhead,
    })
  })
}

export function trackNotebookCreated(params: { notebookCount: number }): void {
  safe(() => {
    capture('notebook_created', { notebook_count: params.notebookCount })
  })
}

export function trackTimerStarted(params: {
  notebookId: string
  durationSec?: number
}): void {
  safe(() => {
    const props: Record<string, unknown> = { notebook_id: params.notebookId }
    if (params.durationSec !== undefined) {
      props.duration_sec = params.durationSec
    }
    capture('timer_started', props)
  })
}

// 初回アクティベーション判定用の localStorage キー。
// 「タスクを進行中にして時間記録が始まった」初回だけを 1 度きりで捉える。
const FIRST_ACTIVATION_KEY = 'donezo-first-activation'

/**
 * 初回アクティベーション = タスクを初めて「進行中」にして時間記録が始まった瞬間。
 * これが Donezo 固有の価値（自動時間記録）の初回体験であり、
 * ファネル「登録 → 初回タスク → 初回“進行中”」の中核指標。
 *
 * その端末で 1 度きり発火する（毎回の timer_started とは別に、初回だけを分離して見る）。
 * timer_started と一緒に呼ぶこと（呼び出しは app-page の handleTaskTimerStart 1 箇所）。
 */
export function trackFirstActivation(params: { notebookId: string }): void {
  if (typeof window === 'undefined') return
  safe(() => {
    if (localStorage.getItem(FIRST_ACTIVATION_KEY)) return
    localStorage.setItem(FIRST_ACTIVATION_KEY, String(Date.now()))
    capture('first_activation', { notebook_id: params.notebookId })
  })
}

export function trackTimerCompleted(params: {
  notebookId: string
  actualDurationSec: number
}): void {
  safe(() => {
    capture('timer_completed', {
      notebook_id: params.notebookId,
      actual_duration_sec: params.actualDurationSec,
    })
  })
}

/**
 * 完了記録 1 件から task_completed (+ 時間記録があれば timer_completed) を送る。
 * 呼び出しは lib/local-task-tracker.ts の markTaskCompleted の 1 箇所
 * (app-page の 3 つの完了経路がすべてそこを通る。旧 unified-editor に配線していた頃の
 * 意味を踏襲: duration は ms、timer_completed は duration > 0 のときだけ)。
 */
export function trackCompletedTaskRecord(params: {
  notebookId: string
  completedTask: CompletedTask
}): void {
  const duration = params.completedTask.task.duration ?? 0
  const durationSec = Math.round(duration / 1000)
  trackTaskCompleted({
    notebookId: params.notebookId,
    hadDuration: duration > 0,
    timeToCompleteSec: duration > 0 ? durationSec : undefined,
  })
  if (duration > 0) {
    trackTimerCompleted({ notebookId: params.notebookId, actualDurationSec: durationSec })
  }
}

export type SignUpProvider = 'email' | 'google' | 'apple' | 'github' | 'unknown'

export function trackSignUpCompleted(params: { provider: SignUpProvider }): void {
  safe(() => {
    capture('sign_up_completed', { provider: params.provider })
  })
}

// ===== 成長施策のファネル (2026-09-18) =====
//
// 見たいファネルは「LP → ゲスト/登録 → 最初のタスクを書く → 翌日再訪」。
// 再訪は PostHog の $pageview (identify 済み) から Retention インサイトで取れるので
// イベントは足さない。定義は docs/analytics-funnel.md を参照。

const FIRST_TASK_KEY = 'donezo-first-task-created'

/**
 * ユーザーが自分で最初のタスクを書いた瞬間 (端末ごとに 1 度きり)。
 * 検出は lib/first-task-detect.ts、呼び出しは lib/sync-manager.ts の queueChanges。
 */
export function trackFirstTaskCreated(params: { notebookId: string }): void {
  if (typeof window === 'undefined') return
  safe(() => {
    if (localStorage.getItem(FIRST_TASK_KEY)) return
    localStorage.setItem(FIRST_TASK_KEY, String(Date.now()))
    capture('first_task_created', { notebook_id: params.notebookId })
  })
}

/** LP の「登録せずに試す」で匿名ユーザーが作られた */
export function trackGuestSessionStarted(params: { location: 'hero' | 'final' }): void {
  safe(() => {
    capture('guest_session_started', { location: params.location })
  })
}

/** ゲスト開始に失敗した (Supabase 側で匿名ログインが無効、など) */
export function trackGuestSessionFailed(params: { reason: string }): void {
  safe(() => {
    capture('guest_session_failed', { reason: params.reason })
  })
}

/** 匿名ユーザーがメール登録 / OAuth 連携で本アカウントに昇格した */
export function trackGuestConverted(params: { provider: SignUpProvider }): void {
  safe(() => {
    capture('guest_converted', { provider: params.provider })
  })
}

// ===== ペイウォール (2026-10-23) =====
//
// 10-01 以降に作られた無課金アカウントは全画面ペイウォールで止まる。表示・購入・離脱の
// イベントが無く、止まった人数を数えられなかった。名前とプロパティは mobile
// (mobile/src/lib/analytics-events.ts) と同じ (__tests__/lib/paywall-event-parity.test.ts が固定)。
// Web の購入は Stripe のリダイレクトを挟むので、完了はここでは取れない (subscriptions テーブルで見る)。

export type PaywallPlan = 'yearly' | 'lifetime'

/** ペイウォールを表示した。storeConfigured = 購入の仕組み (Stripe の価格) が設定されているか */
export function trackPaywallViewed(params: { storeConfigured: boolean }): void {
  safe(() => {
    capture('paywall_viewed', { store_configured: params.storeConfigured })
  })
}

export function trackPaywallPurchaseStarted(params: { plan: PaywallPlan }): void {
  safe(() => {
    capture('paywall_purchase_started', { plan: params.plan })
  })
}

/** ペイウォールでサインアウトを選んだ (離脱) */
export function trackPaywallSignedOut(): void {
  safe(() => {
    capture('paywall_signed_out', {})
  })
}
